import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  specIcons,
  formatMoney,
  formatDate,
  formatSaleTime,
  formatOdometer,
  formatKeyStatus,
  maskVin,
  translateDamage,
  translateTitleType,
  translateCondition,
  translateTransmission,
  translateFuel,
  translateSaleStatus,
  resolveImageUrl,
  toCopartHq,
  toCopartThumb,
  useCountdown,
  Skel,
  orSkel,
} from "./carHelpers.jsx";
import { formatPrice } from "./parseHistory.js";

/**
 * Ported 1:1 from the BidCars frontend (Car.jsx) — same layout, same labels,
 * same spec-block grid. Differences from the main app:
 *   • English-only (no runtime locale)
 *   • No bidding form (plugin-side blocks live below via CarPage.jsx)
 *   • Any missing field renders a neutral skeleton bar (no animation)
 *   • AuctionGate seller / reserve / buy-now fields fold into the Auction
 *     spec-block (variant 2 of the plugin integration).
 *   • `rightColumnSlot` accepts extra content (e.g. compact Carfax widget)
 *     rendered inside the sticky right column under the bid card.
 */
export default function CarDetail({ bidcars, agMeta = null, loading = false, rightColumnSlot = null }) {
  const hasBidcars = Boolean(bidcars && typeof bidcars === "object" && Object.keys(bidcars).length);

  const [mainImg, setMainImg] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const touchStartX = useRef(null);

  // Reset main image when the vehicle changes
  useEffect(() => {
    setMainImg(null);
  }, [bidcars?.id, bidcars?.vin, bidcars?.lotNumber]);

  // ── Images (HQ + thumb pairs) ──────────────────────────────────────────
  const imagesHq = useMemo(() => {
    if (!bidcars) return [];
    const urls = [];
    if (Array.isArray(bidcars.images)) {
      for (const img of bidcars.images) {
        const url = toCopartHq(resolveImageUrl(img));
        if (url && !url.includes("/lotImages") && !url.includes("/api/")) urls.push(url);
      }
    }
    if (urls.length === 0 && bidcars.imageUrl) {
      const url = toCopartHq(resolveImageUrl({ sourceUrl: bidcars.imageUrl, localPath: bidcars.imageUrl }));
      if (url) urls.push(url);
    }
    if (urls.length === 0 && bidcars.imageThumbnail) {
      const url = toCopartHq(resolveImageUrl({ sourceUrl: bidcars.imageThumbnail, localPath: bidcars.imageThumbnail }));
      if (url) urls.push(url);
    }
    if (urls.length === 0 && bidcars.lotImagesUrl) {
      urls.push(String(bidcars.lotImagesUrl));
    }
    if (urls.length === 0 && bidcars.primaryImage) {
      urls.push(String(bidcars.primaryImage));
    }
    const seen = new Set();
    return urls.filter((u) => {
      if (!u || seen.has(u)) return false;
      seen.add(u);
      return true;
    });
  }, [bidcars]);

  const images = useMemo(
    () => imagesHq.map((hq) => ({ hq, thumb: toCopartThumb(hq) || hq })),
    [imagesHq]
  );

  const mainImageUrl = mainImg ?? images[0]?.hq ?? null;

  // ── Countdown & pricing ────────────────────────────────────────────────
  const endTimestamp = bidcars?.saleEndTimestamp != null && Number.isFinite(bidcars.saleEndTimestamp)
    ? Math.floor(bidcars.saleEndTimestamp)
    : null;
  const countdown = useCountdown(endTimestamp);

  const currentBidFromApi = Number(bidcars?.currentBid);
  const currentBidAmount = Number.isFinite(currentBidFromApi) && currentBidFromApi > 0
    ? currentBidFromApi
    : Number(bidcars?.highBid);
  const currentBid = Number.isFinite(currentBidAmount) && currentBidAmount > 0 ? currentBidAmount : null;
  const buyItNowAmount = Number(bidcars?.buyItNowPrice);
  const buyItNowPrice = Number.isFinite(buyItNowAmount) && buyItNowAmount > 0 ? buyItNowAmount : null;

  const SOLD_STATUSES = ["SOLD", "SALE_ENDED", "SALE_ENDED_NOSALE"];
  const isStatusSold = SOLD_STATUSES.some((s) =>
    String(bidcars?.saleStatus ?? "").toUpperCase().includes(s)
  );
  const isClosed = isStatusSold || (endTimestamp !== null && !!countdown?.expired);

  // ── Derived display strings ────────────────────────────────────────────
  const title = [bidcars?.year, bidcars?.make, bidcars?.model]
    .filter(Boolean)
    .join(" ") || bidcars?.title || null;

  const location = bidcars?.locationCity
    ? (bidcars?.locationState ? `${bidcars.locationCity}, ${bidcars.locationState}` : bidcars.locationCity)
    : bidcars?.locationName || null;

  const subTitleParts = [
    location,
    translateFuel(bidcars?.fuelType),
    translateTransmission(bidcars?.transmission),
  ].filter((v) => v && v !== "—");
  const subTitle = subTitleParts.length ? subTitleParts.join(" • ") : null;

  const conditionNode = translateCondition(bidcars?.lotCondition, bidcars?.runStatus);

  // ── Lightbox navigation ────────────────────────────────────────────────
  const openLightbox = useCallback((idx) => {
    setLightboxIdx(idx);
    setLightboxOpen(true);
  }, []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const goNext = useCallback((total) => setLightboxIdx((i) => (i + 1) % total), []);
  const goPrev = useCallback((total) => setLightboxIdx((i) => (i - 1 + total) % total), []);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % (imagesHq.length || 1));
      if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + (imagesHq.length || 1)) % (imagesHq.length || 1));
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxOpen, closeLightbox, imagesHq.length]);

  useEffect(() => {
    if (lightboxOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [lightboxOpen]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback((e, total) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) setLightboxIdx((i) => (i + 1) % total);
      else setLightboxIdx((i) => (i - 1 + total) % total);
    }
    touchStartX.current = null;
  }, []);

  // ── Spec categories (same order as main frontend Car.jsx) ──────────────
  const categories = [
    {
      id: "vehicle",
      title: "Specifications",
      rows: [
        ["car_odometer", "Odometer", formatOdometer(bidcars?.odometerMiles, bidcars?.odometerKm, bidcars?.odometerBrand) || fallbackOdometer(bidcars)],
        ["car_has_key", "Has Key", formatKeyStatus(bidcars?.hasKeys)],
        ["car_engine", "Engine", bidcars?.engine ?? bidcars?.engineCapacity ?? null],
        ["car_drivetrain", "Drivetrain", bidcars?.driveType ?? bidcars?.drive ?? bidcars?.drivetrain ?? null],
        ["car_transmission", "Transmission", translateTransmission(bidcars?.transmission)],
        ["car_fuel", "Fuel", translateFuel(bidcars?.fuelType)],
        ["car_cylinders", "Cylinders", bidcars?.cylinders ?? null],
        ["car_body", "Body", bidcars?.bodyStyle ?? null],
        ["car_color", "Color", bidcars?.color ?? bidcars?.colorExt ?? null],
        ["car_trim", "Trim", bidcars?.trim ?? null],
      ],
    },
    {
      id: "auction",
      title: "Auction",
      rows: [
        ["car_vin", "VIN", maskVin(bidcars?.vin)],
        ["car_lot_number", "Lot Number", bidcars?.lotNumber ?? null],
        ["car_date_added", "Date Added", formatDate(bidcars?.createDate)],
        ["car_auction_date", "Auction Date", formatAuctionDate(bidcars)],
        ["car_sale_name", "Sale Name", bidcars?.saleName ?? bidcars?.yardName ?? null],
        ["car_location", "Location", location],
        ["car_seller", "Seller", agMeta?.lotInfo?.sellerName ?? agMeta?.lotInfo?.sellerCompany ?? bidcars?.seller ?? null],
        ["car_seller_type", "Seller Type", agMeta?.lotInfo?.sellerType ?? bidcars?.sellerType ?? null],
        ["car_seller_type", "Seller Group", agMeta?.lotInfo?.sellerGroup ?? null],
        ["car_retail_value", "Seller Reserve", formatAgMoney(agMeta?.lotInfo?.minimumBid ?? bidcars?.sellerReserve ?? bidcars?.minimumBid)],
        ["car_retail_value", "Buy Now", formatAgMoney(agMeta?.lotInfo?.buyNowPrice)],
        ["car_seller", "Users who bid", bidcars?.bidders ?? null],
      ],
    },
    {
      id: "damage",
      title: "Damage & Condition",
      rows: [
        ["car_primary_damage", "Primary Damage", translateDamage(bidcars?.damagePrimary ?? bidcars?.primaryDamage)],
        ["car_secondary_damage", "Secondary Damage", translateDamage(bidcars?.damageSecondary ?? bidcars?.secondaryDamage)],
        ["car_condition", "Condition", conditionNode],
        ["car_document", "Document", translateTitleType(bidcars?.titleType, bidcars?.saleTitleState) ?? bidcars?.docType ?? bidcars?.document ?? null],
        ["car_retail_value", "Estimated Retail Value", bidcars?.estRetailValue != null && Number(bidcars.estRetailValue) > 0
          ? formatMoney(Number(bidcars.estRetailValue))
          : bidcars?.retailValue != null && Number(bidcars.retailValue) > 0
            ? formatMoney(Number(bidcars.retailValue))
            : null],
      ],
    },
  ];

  const pricingRow = translateSaleStatus(bidcars?.saleStatus);

  return (
    <div className="nd-car-detail">
      <div className="nd-car-detail__top">
        <h1 className="nd-car-detail__title">{title || <Skel w={280} />}</h1>
        <p className="nd-car-detail__sub">{subTitle || <Skel w={240} />}</p>
        <p className="nd-car-detail__meta">
          Lot: {bidcars?.lotNumber || <Skel w={70} />} • VIN: {maskVin(bidcars?.vin) || <Skel w={140} />}
        </p>
      </div>

      <div className="nd-car-detail__hero">
        <section className="nd-car-detail__gallery">
          <div
            className={`nd-car-detail__main-image${mainImageUrl ? " nd-car-detail__main-image--clickable" : ""}`}
            onClick={() => {
              if (!mainImageUrl) return;
              const idx = imagesHq.indexOf(mainImageUrl);
              openLightbox(idx >= 0 ? idx : 0);
            }}
          >
            {mainImageUrl ? (
              <img src={mainImageUrl} alt={title || "vehicle"} />
            ) : (
              <div className="nd-car-detail__no-image">
                {loading || hasBidcars ? <span className="skel skel--block" /> : <span className="muted small">No image</span>}
              </div>
            )}
            {conditionNode && (
              <span className="nd-car-detail__badge">{conditionNode}</span>
            )}
            {isClosed && (
              <span className="nd-car-detail__sold-badge">Auction Ended</span>
            )}
          </div>
          {images.length > 1 ? (
            <div className="nd-car-detail__thumbs">
              {images.slice(0, 8).map(({ hq, thumb }, i) => (
                <button
                  key={hq + i}
                  type="button"
                  className={`nd-car-detail__thumb${mainImageUrl === hq ? " is-active" : ""}`}
                  onClick={() => setMainImg(hq)}
                >
                  <img src={thumb} alt="" />
                </button>
              ))}
            </div>
          ) : (
            <div className="nd-car-detail__thumbs nd-car-detail__thumbs--skel">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="skel skel--thumb" />
              ))}
            </div>
          )}
        </section>

        <aside className="nd-car-detail__bid-panel">
          <div className="nd-car-detail__bid-card">
            {pricingRow && (
              <div className="nd-car-detail__bid-card-head nd-car-detail__bid-card-pricing">
                <div className="nd-car-detail__pricing-item">
                  <div className="nd-car-detail__panel-label">Sale Status</div>
                  <div className="nd-car-detail__price" style={{ fontSize: "1rem" }}>{pricingRow}</div>
                </div>
              </div>
            )}
            <div className="nd-car-detail__bid-card-head">
              <div className="nd-car-detail__panel-label">
                {isClosed ? "Auction Status" : endTimestamp ? "Time until pre-bid" : "Auction Date"}
              </div>
              {countdown?.expired ? (
                <div className="nd-car-detail__countdown nd-car-detail__countdown--closed">Lot Closed</div>
              ) : countdown ? (
                <div className="nd-car-detail__countdown">
                  <div className="nd-car-detail__countdown-box">
                    <span className="nd-car-detail__countdown-val">{countdown.days}</span>
                    <span className="nd-car-detail__countdown-label">Days</span>
                  </div>
                  <div className="nd-car-detail__countdown-box">
                    <span className="nd-car-detail__countdown-val">{String(countdown.hours).padStart(2, "0")}</span>
                    <span className="nd-car-detail__countdown-label">Hours</span>
                  </div>
                  <div className="nd-car-detail__countdown-box">
                    <span className="nd-car-detail__countdown-val">{String(countdown.minutes).padStart(2, "0")}</span>
                    <span className="nd-car-detail__countdown-label">Min</span>
                  </div>
                  <div className="nd-car-detail__countdown-box">
                    <span className="nd-car-detail__countdown-val">{String(countdown.seconds).padStart(2, "0")}</span>
                    <span className="nd-car-detail__countdown-label">Sec</span>
                  </div>
                </div>
              ) : (
                <div className="nd-car-detail__countdown">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="nd-car-detail__countdown-box">
                      <span className="skel skel--line" style={{ width: 24, height: 18 }} />
                      <span className="skel skel--line" style={{ width: 30, height: 8, marginTop: 4 }} />
                    </div>
                  ))}
                </div>
              )}
              {!isClosed && (
                <>
                  <div className="nd-car-detail__panel-label" style={{ marginTop: 12 }}>Current Bid</div>
                  <div className="nd-car-detail__price">
                    {currentBid != null ? formatMoney(currentBid) : <Skel w={110} />}
                  </div>
                </>
              )}
            </div>

            <div className="nd-car-detail__bid-card-body">
              {isClosed ? (
                <div className="nd-car-detail__closed-block">
                  <div className="nd-car-detail__closed-icon">🔒</div>
                  <div className="nd-car-detail__closed-title">Auction Ended</div>
                  <div className="nd-car-detail__closed-desc">
                    Bidding on this lot is closed. Bids and purchases are no longer available.
                  </div>
                </div>
              ) : buyItNowPrice != null ? (
                <>
                  <div className="nd-car-detail__panel-label">Buy Now</div>
                  <div className="nd-car-detail__price">{formatMoney(buyItNowPrice)}</div>
                </>
              ) : (
                <>
                  <div className="nd-car-detail__panel-label">Buy Now</div>
                  <Skel w={140} />
                </>
              )}
            </div>
          </div>
          {rightColumnSlot && (
            <div className="nd-car-detail__side-slot">{rightColumnSlot}</div>
          )}
        </aside>
      </div>

      <div className="nd-car-detail__specs">
        <div className="nd-car-detail__specs-grid nd-car-detail__specs-grid--left-right">
          {categories.map((cat) => (
            <div key={cat.id} className={`nd-car-detail__spec-block nd-car-detail__spec-block--${cat.id}`}>
              <h3 className="nd-car-detail__spec-block-title">{cat.title}</h3>
              <dl className="nd-car-detail__spec-block-list">
                {cat.rows.map(([iconKey, label, value]) => (
                  <div key={label} className="nd-car-detail__spec-block-row">
                    <dt>
                      {specIcons[iconKey] && (
                        <span className="nd-car-detail__spec-icon" aria-hidden="true">
                          {specIcons[iconKey]}
                        </span>
                      )}
                      {label}
                    </dt>
                    <dd>{orSkel(value, skelWidthFor(iconKey))}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>

      {lightboxOpen && imagesHq.length > 0 && (
        <div
          className="car-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="car-lightbox__close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>
          <div
            className="car-lightbox__img-wrap"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd(e, imagesHq.length)}
          >
            <img src={imagesHq[lightboxIdx]} alt={`${title || "photo"} ${lightboxIdx + 1}`} />
          </div>
          {imagesHq.length > 1 && (
            <>
              <button
                type="button"
                className="car-lightbox__arrow car-lightbox__arrow--prev"
                onClick={(e) => { e.stopPropagation(); goPrev(imagesHq.length); }}
                aria-label="Previous photo"
              >
                ‹
              </button>
              <button
                type="button"
                className="car-lightbox__arrow car-lightbox__arrow--next"
                onClick={(e) => { e.stopPropagation(); goNext(imagesHq.length); }}
                aria-label="Next photo"
              >
                ›
              </button>
              <div className="car-lightbox__counter">{lightboxIdx + 1} / {imagesHq.length}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────
function formatAuctionDate(bidcars) {
  if (!bidcars?.saleDate) return null;
  const d = formatDate(bidcars.saleDate);
  if (!d) return null;
  const t = formatSaleTime(bidcars.saleTime);
  const tz = bidcars.timezone ? ` (${bidcars.timezone})` : "";
  return `${d}${t ? ` ${t}` : ""}${tz}`.trim();
}

function fallbackOdometer(bidcars) {
  const raw = bidcars?.odometer ?? bidcars?.odometerBrand ?? null;
  if (raw == null) return null;
  if (typeof raw === "number") return `${raw.toLocaleString()} mi`;
  return String(raw);
}

function formatAgMoney(raw) {
  if (raw == null || raw === "" || raw === "0") return null;
  const num = Number(raw);
  if (!Number.isFinite(num) || num <= 0) return null;
  return `$${formatPrice(num)}`;
}

function skelWidthFor(iconKey) {
  switch (iconKey) {
    case "car_vin":
      return 160;
    case "car_auction_date":
    case "car_date_added":
      return 130;
    case "car_retail_value":
      return 90;
    case "car_odometer":
      return 110;
    default:
      return 80;
  }
}
